import React, { useState, useEffect, useMemo } from 'react';
import * as apiClient from '../services/apiClient';
import { Search, CreditCard, Check, ChevronDown, Building2, Sparkles } from 'lucide-react';

/**
 * MasterCardPicker
 * Reusable searchable dropdown / picker for selecting a Master Credit Card from the catalog.
 */
export default function MasterCardPicker({
  selectedCardId,
  onSelect,
  placeholder = "Select or search UAE Credit Card...",
  label = "Official Card Catalog (Rewards & Community)"
}) {
  const [cards, setCards] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadCatalog() {
      setLoading(true);
      try {
        const [banksRes, cardsRes] = await Promise.all([
          apiClient.request('/catalog/banks'),
          apiClient.request('/catalog/cards?limit=250&sort=name_asc')
        ]);
        if (isMounted) {
          setBanks(Array.isArray(banksRes) ? banksRes : []);
          setCards(cardsRes?.cards || []);
        }
      } catch (err) {
        console.error('Failed to load catalog for picker:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadCatalog();
    return () => { isMounted = false; };
  }, []);

  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      if (selectedBank && card.bank_name !== selectedBank) return false;
      if (searchTerm && searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = (card.card_name || '').toLowerCase().includes(q);
        const matchBank = (card.bank_name || '').toLowerCase().includes(q);
        const matchTier = (card.card_tier || '').toLowerCase().includes(q);
        return matchName || matchBank || matchTier;
      }
      return true;
    });
  }, [cards, selectedBank, searchTerm]);

  const selectedCard = useMemo(() => {
    if (!selectedCardId) return null;
    return cards.find(c => c.card_id === selectedCardId);
  }, [cards, selectedCardId]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <CreditCard size={14} color="var(--accent-purple)" />
        {label}
      </label>

      {/* Selected Box / Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 14px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: isOpen ? '1px solid var(--accent-purple)' : '1px solid var(--border-glass)',
          borderRadius: '12px',
          color: selectedCard ? 'var(--text-main)' : 'var(--text-muted)',
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          textAlign: 'left',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            background: selectedCard ? 'rgba(165,94,234,0.2)' : 'rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <CreditCard size={16} color={selectedCard ? 'var(--accent-purple)' : 'var(--text-muted)'} />
          </div>
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedCard ? (
              <div>
                <span style={{ fontWeight: '700', color: 'var(--text-main)' }}>{selectedCard.card_name}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>{selectedCard.bank_name}</span>
              </div>
            ) : (
              <span>{loading ? 'Loading catalog...' : placeholder}</span>
            )}
          </div>
        </div>
        <ChevronDown size={16} color="var(--text-muted)" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="glass-panel"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: '14px',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-secondary, #1a1d2e)',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)',
            maxHeight: '380px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header Controls: Bank Filter + Search */}
          <div style={{ padding: '12px', borderBottom: '1px solid var(--border-glass)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Search Input */}
            <div style={{ position: 'relative' }}>
              <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: '10px', top: '10px' }} />
              <input
                type="text"
                placeholder="Search by card name, bank, or tier..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px 8px 32px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            {/* Bank Filter Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Building2 size={14} color="var(--text-muted)" />
              <select
                value={selectedBank}
                onChange={e => setSelectedBank(e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '11px'
                }}
              >
                <option value="">All Banks ({banks.length})</option>
                {banks.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cards List */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '6px' }}>
            {filteredCards.length === 0 ? (
              <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                No cards match your search.
              </div>
            ) : (
              filteredCards.map(card => {
                const isSelected = card.card_id === selectedCardId;
                return (
                  <div
                    key={card.card_id}
                    onClick={() => {
                      onSelect(card.card_id, card);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(165,94,234,0.15)' : 'transparent',
                      border: isSelected ? '1px solid rgba(165,94,234,0.3)' : '1px solid transparent',
                      marginBottom: '4px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: isSelected ? 'var(--accent-purple)' : 'var(--text-main)' }}>
                          {card.card_name}
                        </span>
                        {card.card_tier && (
                          <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)' }}>
                            {card.card_tier}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                        {card.bank_name} • Fee: {card.annual_fee === 0 ? 'Free' : `AED ${card.annual_fee}`}
                      </span>
                    </div>
                    {isSelected && (
                      <Check size={16} color="var(--accent-purple)" style={{ flexShrink: 0, marginLeft: '8px' }} />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Quick Clear / Custom Link footer */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-glass)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.1)' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Showing {filteredCards.length} cards
            </span>
            {selectedCardId && (
              <button
                type="button"
                onClick={() => { onSelect(null, null); setIsOpen(false); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
